import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Select, Skeleton, message } from "antd";
import { CheckCircleOutlined, MinusCircleOutlined, RollbackOutlined } from "@ant-design/icons";
import BrandTemplateApi, {
  BrandTemplateDto,
  BrandTemplateSizeUnit,
  TemplateVisibility,
} from "../../../../../api/BrandTemplateApi";
import { Button as MhmButton } from "../../../misc/Button";
import { env } from "../../../../../env/env";
import RendererThumb from "../../../../common/RendererThumb";
import BulkActionBar from "../../../../common/BulkActionBar";
import SavedTemplateCard from "./SavedTemplateCard";
import CopyToBrandKitModal from "./CopyToBrandKitModal";
import CopyToBrandKitIcon from "../../../../icons/CopyToBrandKitIcon";
import DeleteDesignIcon from "../../../../icons/DeleteDesignIcon";
import "./TemplatesTab.scss";
import { getSizeLabel } from "../../../../../util/utils";

type Mode = "saved" | "create";

interface TemplateOption {
  productType: string;
  label: string;
  thumb:
    | "menu"
    | "menu-tabloid"
    | "menu-custom"
    | "menu-letter"
    | "menu-legal"
    | "menu-a4"
    | "dmb-landscape"
    | "dmb-portrait";
}

const MENU_OPTIONS: TemplateOption[] = [
  { productType: "custom", label: "Custom Size", thumb: "menu-custom" },
  { productType: "menu-letter", label: "Letter Menu", thumb: "menu-letter" },
  { productType: "menu-legal", label: "Legal Menu", thumb: "menu-legal" },
  { productType: "menu-tabloid", label: "Tabloid Menu", thumb: "menu-tabloid" },
  { productType: "menu-a4", label: "A4 Menu", thumb: "menu-a4" },
];

const DMB_OPTIONS: TemplateOption[] = [
  { productType: "digital-menu-board", label: "Landscape", thumb: "dmb-landscape" },
  { productType: "digital-menu-board-portrait", label: "Portrait", thumb: "dmb-portrait" },
];

function TemplateThumb({ variant }: { variant: TemplateOption["thumb"] }) {
  switch (variant) {
    case "menu-custom":
      return (
        <img
          src={`${env.dashboardPath}/brand-templates/custom-size.png`}
          alt="Custom size menu"
          className="template-thumb-img"
        />
      );
    case "menu-letter":
      return (
        <img
          src={`${env.dashboardPath}/brand-templates/letter-menu.png`}
          alt="Letter menu"
          className="template-thumb-img"
        />
      );
    case "menu-legal":
      return (
        <img
          src={`${env.dashboardPath}/brand-templates/legal-menu.png`}
          alt="Legal menu"
          className="template-thumb-img"
        />
      );
    case "menu-a4":
      return (
        <img
          src={`${env.dashboardPath}/brand-templates/a4-menu.svg`}
          alt="A4 menu"
          className="template-thumb-img"
        />
      );
    case "menu-tabloid":
      return (
        <img
          src={`${env.dashboardPath}/brand-templates/tabloid-menu.png`}
          alt="Tabloid menu"
          className="template-thumb-img"
        />
      );
    case "dmb-landscape":
      return (
        <img
          src={`${env.dashboardPath}/brand-templates/dmb-landscape.png`}
          alt="Landscape digital menu board"
          className="template-thumb-img"
        />
      );
    case "dmb-portrait":
      return (
        <img
          src={`${env.dashboardPath}/brand-templates/dmb-portrait.png`}
          alt="Portrait digital menu board"
          className="template-thumb-img template-thumb-img--portrait"
        />
      );
    case "menu":
    default:
      return (
        <svg viewBox="0 0 80 110" className="template-thumb-svg template-thumb-svg--portrait">
          <rect x="10" y="8" width="60" height="94" rx="2" fill="#fff" stroke="#333" strokeWidth="1" />
          <rect x="22" y="18" width="36" height="6" rx="1" fill="#bbb" />
          <rect x="16" y="34" width="48" height="2" fill="#333" />
          <rect x="16" y="42" width="36" height="2" fill="#bbb" />
          <rect x="56" y="42" width="8" height="2" fill="#333" />
          <rect x="16" y="50" width="36" height="2" fill="#bbb" />
          <rect x="56" y="50" width="8" height="2" fill="#333" />
          <rect x="16" y="58" width="36" height="2" fill="#bbb" />
          <rect x="56" y="58" width="8" height="2" fill="#333" />
          <rect x="16" y="66" width="36" height="2" fill="#bbb" />
          <rect x="56" y="66" width="8" height="2" fill="#333" />
          <rect x="16" y="82" width="36" height="2" fill="#bbb" />
          <rect x="56" y="82" width="8" height="2" fill="#333" />
        </svg>
      );
  }
}

function SavedTemplateThumb({
  id,
  name,
  fallbackVariant,
}: {
  id: string | undefined;
  name: string;
  fallbackVariant: TemplateOption["thumb"];
}) {
  return (
    <RendererThumb
      id={id}
      alt={name}
      className="template-thumb-img"
      fallback={<TemplateThumb variant={fallbackVariant} />}
    />
  );
}

interface Props {
  brandKitId: string;
  locationId?: number;
}

export function TemplatesTab({ brandKitId, locationId }: Props) {
  const [mode, setMode] = useState<Mode>("saved");
  const [templates, setTemplates] = useState<BrandTemplateDto[]>([]);
  const [loading, setLoading] = useState(false);

  const [customSizeOpen, setCustomSizeOpen] = useState(false);
  const [customWidth, setCustomWidth] = useState("");
  const [customHeight, setCustomHeight] = useState("");
  const [customUnit, setCustomUnit] = useState<BrandTemplateSizeUnit>("IN");
  const [customSubmitting, setCustomSubmitting] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<BrandTemplateDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyTargets, setCopyTargets] = useState<BrandTemplateDto[]>([]);

  const selectMode = selectedIds.length > 0;

  const load = useCallback(() => {
    if (!brandKitId) return;
    setLoading(true);
    BrandTemplateApi.listByBrandKit(brandKitId)
      .then((resp) => setTemplates(resp.data || []))
      .catch(() => message.error("Failed to load templates"))
      .finally(() => setLoading(false));
  }, [brandKitId]);

  useEffect(() => {
    load();
  }, [load]);

  const redirectToNewBrandTemplate = (
    productType: string,
    custom?: { width: number; height: number; unit: BrandTemplateSizeUnit }
  ) => {
    const params = new URLSearchParams();
    params.set("newBrandTemplate", "true");
    params.set("brandKitId", brandKitId);
    if (locationId != null) params.set("locationId", String(locationId));
    params.set("size", productType);
    if (custom) {
      params.set("width", String(custom.width));
      params.set("height", String(custom.height));
      params.set("unit", custom.unit.toLowerCase());
    }
    window.location.href = `${env.timberContextPath}/design?${params.toString()}`;
  };

  const handleTileClick = (opt: TemplateOption) => {
    if (opt.productType === "custom") {
      setCustomWidth("");
      setCustomHeight("");
      setCustomUnit("IN");
      setCustomSizeOpen(true);
      return;
    }
    redirectToNewBrandTemplate(opt.productType);
  };

  const handleCustomSizeCreate = () => {
    const widthNum = parseFloat(customWidth);
    const heightNum = parseFloat(customHeight);
    const min = customUnit === "PX" ? 96 : 1;
    const max = customUnit === "PX" ? 8000 : 83.328;
    const errorMessage =
      customUnit === "PX"
        ? "Dimensions must be between 96px and 8000px."
        : "Dimensions must be between 1in and 83.328in.";
    const isInvalid = (n: number) => !Number.isFinite(n) || n < min || n > max;
    if (isInvalid(widthNum) || isInvalid(heightNum)) {
      message.error(errorMessage);
      return;
    }
    setCustomSubmitting(true);
    redirectToNewBrandTemplate("custom", {
      width: widthNum,
      height: heightNum,
      unit: customUnit,
    });
  };

  const sanitizeNumericInput = (raw: string) => {
    const cleaned = raw.replace(/[^0-9.]/g, "");
    const firstDot = cleaned.indexOf(".");
    if (firstDot === -1) return cleaned;
    return (
      cleaned.slice(0, firstDot + 1) +
      cleaned.slice(firstDot + 1).replace(/\./g, "")
    );
  };

  const openInEditor = (template: BrandTemplateDto) => {
    if (!template.id) return;
    const params = new URLSearchParams();
    params.set("id", template.id);
    if (locationId != null) params.set("locationId", String(locationId));
    window.location.href = `${env.timberContextPath}/design?${params.toString()}`;
  };

  const handleRenamed = (id: string, newName: string) => {
    setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, name: newName } : t)));
  };

  const applyVisibility = (ids: string[], visibility: TemplateVisibility) => {
    setTemplates((prev) =>
      prev.map((t) => (t.id && ids.includes(t.id) ? { ...t, visibility } : t))
    );
  };

  const persistVisibility = async (
    ids: string[],
    visibility: TemplateVisibility,
    previous: Map<string, TemplateVisibility>
  ) => {
    try {
      const resp = await BrandTemplateApi.setVisibility(ids, visibility);
      if (!resp.ok) throw new Error();
    } catch {
      setTemplates((prev) =>
        prev.map((t) => (t.id && previous.has(t.id) ? { ...t, visibility: previous.get(t.id) } : t))
      );
      message.error("Failed to update visibility");
    }
  };

  const snapshotVisibility = (ids: string[]): Map<string, TemplateVisibility> =>
    new Map(
      templates
        .filter((t) => t.id && ids.includes(t.id))
        .map((t) => [t.id!, t.visibility === "draft" ? "draft" : "live"])
    );

  const handleSetVisibility = (template: BrandTemplateDto, visibility: TemplateVisibility) => {
    if (!template.id) return;
    const previous = snapshotVisibility([template.id]);
    applyVisibility([template.id], visibility);
    message.success(visibility === "live" ? "Template is now live" : "Template moved to draft");
    persistVisibility([template.id], visibility, previous);
  };

  const handleDelete = (template: BrandTemplateDto) => {
    if (!template.id) return;
    setDeleteTarget(template);
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
  };

  const submitDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      const resp = await BrandTemplateApi.delete(deleteTarget.id);
      if (!resp.ok) {
        message.error("Failed to delete template");
        return;
      }
      setTemplates((prev) => prev.filter((t) => t.id !== deleteTarget.id));
      message.success("Template deleted");
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const onSelect = (template: BrandTemplateDto, checked: boolean) => {
    if (!template.id) return;
    setSelectedIds((prev) =>
      checked ? [...prev, template.id!] : prev.filter((id) => id !== template.id)
    );
  };

  const clearSelection = () => setSelectedIds([]);

  const selectedTemplates = useMemo(
    () => templates.filter((t) => t.id && selectedIds.includes(t.id)),
    [templates, selectedIds]
  );

  const openCopyModal = (targets: BrandTemplateDto[]) => {
    if (targets.length === 0) return;
    setCopyTargets(targets);
    setCopyModalOpen(true);
  };

  const onBulkSetVisibility = (visibility: TemplateVisibility) => {
    const ids = selectedTemplates.map((t) => t.id!);
    if (ids.length === 0) return;
    const previous = snapshotVisibility(ids);
    applyVisibility(ids, visibility);
    message.success(
      visibility === "live"
        ? `${ids.length} template${ids.length === 1 ? "" : "s"} now live`
        : `${ids.length} template${ids.length === 1 ? "" : "s"} moved to draft`
    );
    clearSelection();
    persistVisibility(ids, visibility, previous);
  };

  const onBulkRemove = () => {
    const ids = selectedTemplates.map((t) => t.id!);
    if (ids.length === 0) return;
    Modal.confirm({
      title: ids.length === 1 ? "Remove from Brand Templates?" : `Remove ${ids.length} from Brand Templates?`,
      content:
        "These designs will move back to Saved Designs. They won't be available as templates anymore, but nothing else will change.",
      okText: "Remove from Brand Templates",
      onOk: async () => {
        const resp = await BrandTemplateApi.removeFromBrandTemplatesBulk(ids);
        if (!resp.ok) {
          message.error("Failed to remove from Brand Templates");
          return;
        }
        const removed: string[] = await resp.json().catch(() => ids);
        const removedIds = removed.length ? removed : ids;
        setTemplates((prev) => prev.filter((t) => !removedIds.includes(t.id!)));
        message.success(`${removedIds.length} removed from Brand Templates`);
        clearSelection();
      },
    });
  };

  const onBulkDelete = () => {
    const ids = selectedTemplates.map((t) => t.id!);
    if (ids.length === 0) return;
    Modal.confirm({
      title: ids.length === 1 ? "Delete brand template?" : `Delete ${ids.length} brand templates?`,
      content: "This cannot be undone. Designs created from them will not be affected.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        const resp = await BrandTemplateApi.deleteBulk(ids);
        if (!resp.ok) {
          message.error("Failed to delete templates");
          return;
        }
        const removed: string[] = await resp.json().catch(() => ids);
        const removedIds = removed.length ? removed : ids;
        setTemplates((prev) => prev.filter((t) => !removedIds.includes(t.id!)));
        message.success(`${removedIds.length} template${removedIds.length === 1 ? "" : "s"} deleted`);
        clearSelection();
      },
    });
  };

  const saved = useMemo(() => templates, [templates]);

  return (
    <div className="templates-tab">
      {mode === "create" ? (
        <>
          <button
            type="button"
            className="templates-back-link"
            onClick={() => setMode("saved")}
          >
            &lt; Back
          </button>

          <h3 className="templates-section-heading">Create New...</h3>

          <section className="templates-section">
            <h4>Menu</h4>
            <div className="templates-grid">
              {MENU_OPTIONS.map((opt) => (
                <button
                  key={opt.productType}
                  type="button"
                  className="template-tile"
                  onClick={() => handleTileClick(opt)}
                >
                  <div className="template-tile-label">{opt.label}</div>
                  <div className="template-tile-thumb">
                    <TemplateThumb variant={opt.thumb} />
                  </div>
                </button>
              ))}
            </div>
          </section>

          <section className="templates-section">
            <h4>Digital Menu Board</h4>
            <div className="templates-grid">
              {DMB_OPTIONS.map((opt) => (
                <button
                  key={opt.productType}
                  type="button"
                  className="template-tile"
                  onClick={() => handleTileClick(opt)}
                >
                  <div className="template-tile-label">{opt.label}</div>
                  <div className="template-tile-thumb">
                    <TemplateThumb variant={opt.thumb} />
                  </div>
                </button>
              ))}
            </div>
          </section>
        </>
      ) : (
        <>
          <div className="templates-saved-header">
            <h3 className="templates-section-heading">Saved Brand Templates</h3>
            {saved.length > 0 && (
              <button
                type="button"
                className="templates-new-btn"
                onClick={() => setMode("create")}
              >
                + New Brand Template
              </button>
            )}
          </div>

          {loading ? (
            <Skeleton active />
          ) : saved.length === 0 ? (
            <div className="templates-empty">
              <p>You don&apos;t have any saved brand templates yet.</p>
              <button
                type="button"
                className="templates-new-btn"
                onClick={() => setMode("create")}
              >
                + New Brand Template
              </button>
            </div>
          ) : (
            <div className="templates-saved-grid">
              {saved.map((t) => {
                const variant: TemplateOption["thumb"] =
                  t.productType === "menu-tabloid"
                    ? "menu-tabloid"
                    : t.productType.includes("custom")
                    ? "menu-custom"
                    : t.productType === "menu-letter"
                    ? "menu-letter"
                    : t.productType === "menu-legal"
                    ? "menu-legal"
                    : t.productType === "menu-a4"
                    ? "menu-a4"
                    : t.productType === "digital-menu-board"
                    ? "dmb-landscape"
                    : t.productType === "digital-menu-board-portrait"
                    ? "dmb-portrait"
                    : "menu";
                return (
                  <SavedTemplateCard
                    key={t.id}
                    template={t}
                    brandKitId={brandKitId}
                    productLabel={t.productType === "custom" ? "Custom Size" : getSizeLabel(t.productType)}
                    thumb={<SavedTemplateThumb id={t.id} name={t.name} fallbackVariant={variant} />}
                    onEdit={openInEditor}
                    onRenamed={handleRenamed}
                    onDelete={handleDelete}
                    onRemoved={(removed) =>
                      setTemplates((prev) => prev.filter((x) => x.id !== removed.id))
                    }
                    onCopied={load}
                    onCopyToBrandKit={(tpl) => openCopyModal([tpl])}
                    onSetVisibility={handleSetVisibility}
                    selectMode={selectMode}
                    selected={!!t.id && selectedIds.includes(t.id)}
                    onSelect={onSelect}
                  />
                );
              })}
            </div>
          )}
        </>
      )}

      <Modal
        className="new-brand-modal clean one template-custom-size-modal"
        open={customSizeOpen}
        onCancel={() => {
          if (customSubmitting) return;
          setCustomSizeOpen(false);
        }}
        footer={null}
        maskClosable={!customSubmitting}
      >
        <header>
          <h2>Create custom size</h2>
        </header>

        <div className="template-custom-size-body">
          <Input
            size="large"
            placeholder="Width"
            value={customWidth}
            onChange={(e) => setCustomWidth(sanitizeNumericInput(e.target.value))}
            inputMode="decimal"
            autoFocus
          />
          <span className="template-custom-size-x">×</span>
          <Input
            size="large"
            placeholder="Height"
            value={customHeight}
            onChange={(e) => setCustomHeight(sanitizeNumericInput(e.target.value))}
            onPressEnter={handleCustomSizeCreate}
            inputMode="decimal"
          />
          <Select
            size="large"
            className="template-custom-size-unit"
            value={customUnit}
            onChange={setCustomUnit}
            options={[
              { value: "IN", label: "in" },
              { value: "PX", label: "px" },
            ]}
          />
        </div>

        <footer>
          <MhmButton
            type="Cancel"
            onClick={() => setCustomSizeOpen(false)}
            disabled={customSubmitting}
          >
            <span>Cancel</span>
          </MhmButton>
          <MhmButton
            type="Primary"
            onClick={handleCustomSizeCreate}
            loading={customSubmitting}
            disabled={!customWidth.trim() || !customHeight.trim()}
          >
            <span>Create</span>
          </MhmButton>
        </footer>
      </Modal>

      <Modal
        className="clean one"
        open={!!deleteTarget}
        onCancel={closeDelete}
        footer={null}
        maskClosable={!deleting}
      >
        <header>
          <h2>Are you sure?</h2>
        </header>

        <p>
          <b>{deleteTarget?.name || "This brand template"}</b> will be permanently deleted.
          Designs created from this template will not be affected.
        </p>

        <footer>
          <Button size="large" onClick={closeDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button
            size="large"
            className="primary"
            onClick={submitDelete}
            loading={deleting}
          >
            Delete
          </Button>
        </footer>
      </Modal>

      <BulkActionBar
        visible={mode === "saved" && selectMode}
        count={selectedIds.length}
        onCancel={clearSelection}
        actions={[
          {
            key: "make-live",
            icon: <CheckCircleOutlined />,
            onClick: () => onBulkSetVisibility("live"),
            title: "Make live",
          },
          {
            key: "revert-draft",
            icon: <RollbackOutlined />,
            onClick: () => onBulkSetVisibility("draft"),
            title: "Revert to draft",
          },
          {
            key: "copy",
            icon: <CopyToBrandKitIcon />,
            onClick: () => openCopyModal(selectedTemplates),
            title: "Copy to Brand Kit",
          },
          {
            key: "remove",
            icon: <MinusCircleOutlined />,
            onClick: onBulkRemove,
            title: "Remove from Brand Templates",
          },
          {
            key: "delete",
            icon: <DeleteDesignIcon />,
            onClick: onBulkDelete,
            title: "Delete",
          },
        ]}
      />

      <CopyToBrandKitModal
        open={copyModalOpen}
        templates={copyTargets}
        currentBrandKitId={brandKitId}
        onClose={() => setCopyModalOpen(false)}
        onCopied={clearSelection}
      />
    </div>
  );
}
